import { faFilterSlash } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StackedBarChart } from '@repo/videa-components/src/common-charts/index';
import { cn } from '@repo/videa-components/src/lib/utils';
import { useAtomValue } from 'jotai';

import { usePreferences } from '@/react-query/usePreferences';

import { chartColors } from '../../../../../../packages/videa-components/src/common-charts/themes/colors';
import { isCountsAtom } from '../atoms';
import { TrendIndicator } from '../components/TrendIndicator';
import { useOverallCountsByTx } from '../helpers/benchmarking/overallCountsByTx';
import { useOverallRates } from '../helpers/benchmarking/overallRates';
import { formatLargeNumber } from '../helpers/formatNumber';

export const AIOppAvailableChart = ({
  practices,
  providers,
}: {
  practices?: string[];
  providers?: string[];
}) => {
  const isCounts = useAtomValue(isCountsAtom);

  const { treatmentOpportunity, isLoading } = useOverallCountsByTx({
    practices,
    providers,
  });
  const totalOpp = useOverallRates({
    practices,
    providers,
  });

  const { data: prefs } = usePreferences();
  const isEngagementMetric = prefs?.enableInsightsEngagementMetric;

  return (
    <div className="vai-bg-white vai-rounded-lg vai-p-4 vai-border-gray-200 vai-border-[1px] vai-h-full vai-flex vai-flex-col vai-gap-2">
      <div className="vai-flex vai-flex-col">
        <span className="vai-typography-h4">AI Opportunity Available</span>
        <span className="vai-typography-muted">
          Potential value of unplanned AI recommendations
        </span>
      </div>
      {!!treatmentOpportunity?.length && !isLoading && (
        <>
          <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
            <span className="vai-typography-h2 vai-font-semi-bold">
              {formatLargeNumber(totalOpp?.opportunity, isCounts ? '' : '$')}
            </span>
            <TrendIndicator
              value={
                totalOpp?.opportunity && totalOpp?.previousOpportunity
                  ? Math.round(
                      ((totalOpp?.opportunity - totalOpp?.previousOpportunity) /
                        totalOpp?.previousOpportunity) *
                        100,
                    )
                  : undefined
              }
              suffix="%"
              isNeutral={true}
            />
            <span className="vai-typography-muted">vs. prev period</span>
          </div>
          <div className="vai-flex vai-flex-col vai-gap-2 vai-items-start">
            <div className="vai-flex vai-flex-row vai-items-center vai-gap-4">
              <div className="vai-flex vai-flex-row vai-items-center vai-gap-1">
                <span className="vai-bg-chart-normal-1 vai-w-3 vai-h-3 vai-rounded-[2px]" />
                <span className="vai-typography-muted">Production</span>
              </div>
              <div className="vai-flex vai-flex-row vai-items-center vai-gap-1">
                <span className="vai-bg-chart-tint-1 vai-w-3 vai-h-3 vai-rounded-[2px]" />
                <span className="vai-typography-muted">AI Opportunity</span>
              </div>
            </div>
          </div>
          <StackedBarChart
            data={[
              {
                name: 'AI Opportunity Available',
                data: treatmentOpportunity.map((tx) => tx.opportunity),
              },
              {
                name: 'Production',
                data: treatmentOpportunity.map((tx) => tx.completed),
              },
            ]}
            chartOptions={{
              colors: [chartColors['chart-tint'][1], chartColors.chart[1]],
              unselect: false,
              height: isEngagementMetric ? 200 : 250,
              xAxisCategories: treatmentOpportunity.map((tx) => tx.tx),
              yAxisFormatter: function (this: any) {
                return formatLargeNumber(
                  Math.abs(this.value),
                  isCounts ? '' : '$',
                );
              },
              legendDisabled: true,
              tooltipFormatter: function (this: any) {
                return `${this.series.userOptions.name}: ${formatLargeNumber(
                  Math.abs(this.y),
                  isCounts ? '' : '$',
                )}`;
              },
            }}
          />
        </>
      )}
      {!isLoading && !treatmentOpportunity?.length && (
        <div className="vai-flex vai-gap-1 vai-flex-col vai-items-center vai-justify-center vai-flex-1">
          <FontAwesomeIcon
            icon={faFilterSlash}
            className="vai-h-10 vai-w-10 vai-text-zinc-300 vai-p-2"
          />
          <span className="vai-text-lg vai-text-muted-foreground">No data</span>
          <span className="vai-typography-muted">
            Adjust your filters and try again
          </span>
        </div>
      )}
      {isLoading && (
        <div className="vai-flex vai-p-4 vai-h-full vai-w-full">
          <img
            src="/assets/images/insights/aiOppLoading.png"
            alt="loading"
            className={cn(`vai-w-full vai-h-[275px] vai-opacity-20 `, {
              'vai-h-[200px]': isEngagementMetric,
              'vai-h-[275px]': !isEngagementMetric,
            })}
          />
        </div>
      )}
    </div>
  );
};
